import * as THREE from "three";
// import * as dat from 'dat.gui';
import * as PostProcessing from "demo/postprocessing";
import ContrastShader from 'demo/shaders/ContrastShader';
import { DEMO_LENGTH, ROW_RATE, ROWS_PER_BEAT } from 'demo/config';
import Scene from './Scene';

const properties = {
  defaultBackground: "#000000",
  lightColor1: "#ffffff",
  lightColor2: "#ffffff",
  lightColor3: "#ffffff",
  lightColor4: "#ffffff",
  lightColor5: "#ffffff",
  lightColor6: "#ffffff",
  lightColor7: "#ffffff",
  lightIntensity1: 0.3,
  lightIntensity2: 0.3,
  lightIntensity3: 0.3,
  lightIntensity4: 0.3,
  lightIntensity5: 0.3,
  lightIntensity6: 0.3,
  lightIntensity7: 0.3,
};

/**
 * Row length in ms
 * @type {number}
 */
const ROW_LENGTH = (1 / ROW_RATE) * 1000;
const BEATS_PER_BAR = 32;

/**
 * Scene from demo "alpha"
 */
export default class AlphaScene extends Scene {

  /**
   * @param renderer THREE.WebGLRenderer
   */
  constructor(renderer, syncDevice) {
    super(renderer, syncDevice);
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(65, window.innerWidth / window.innerHeight, 1, 100000);
    this.lookAt = new THREE.Vector3(0, 0, 0);

    let mesh;
    let geometry;
    let material = new THREE.MeshLambertMaterial({ color: 0xFFFFFF });
    material.emissive = new THREE.Color('rgb(198,229,217)');
    material.emissiveIntensity = 0.1;
    material.flatShading = true;

    let x, y, z, w, h, d;
    for (let i = 0; i < 3; i++) {
      w = (i == 0 ? 8004 : (i == 1 ? 250 : 300));
      h = (i == 1 ? 6040 : (i == 2 ? 250 : 300));
      d = (i == 2 ? 21400 : (i == 0 ? 150 : 200));
      for (let k = 0; k < 40; k++) {
        x = i == 0
          ? 4050 + Math.cos(k / 9) * Math.sin(k / 4) * Math.tan(k / 8) * 10000
          : 2100 + Math.sin(k / 8) * Math.tan(k / .3) * Math.cos(-k / 8) * 5000;
        y = i == 1
          ? 4200 + Math.cos(k / 8) * Math.tan(k / 40) * 10000
          : 1800 + Math.sin(k / 9) * Math.tan(k) * 2000;
        z = i == 2
          ? 4100 + Math.sin(k / 8) * 5000
          : 2200 + Math.cos(k / 9) * Math.tan(k / 30) * 2000;
        geometry = new THREE.BoxGeometry(w, h, d);
        mesh = new THREE.Mesh(geometry, material);
        mesh.position.x = x;
        mesh.position.y = y;
        mesh.position.z = z;
        this.scene.add(mesh);
      }
    }
    this.scene.fog = new THREE.Fog(new THREE.Color(properties.defaultBackground), 1000, 10000);

    const light1 = new THREE.PointLight(new THREE.Color(properties.lightColor1), properties.lightIntensity1);
    light1.position.set(0, 0, 0);
    this.scene.add(light1);
    const light2 = new THREE.PointLight(new THREE.Color(properties.lightColor2), properties.lightIntensity2);
    light2.position.set(10000, 1000, 0);
    this.scene.add(light2);
    const light3 = new THREE.PointLight(new THREE.Color(properties.lightColor3), properties.lightIntensity3);
    light3.position.set(-10000, -1000, 0);
    this.scene.add(light3);
    const light4 = new THREE.PointLight(new THREE.Color(properties.lightColor4), properties.lightIntensity4);
    light4.position.set(1000, 10000, 0);
    this.scene.add(light4);
    const light5 = new THREE.PointLight(new THREE.Color(properties.lightColor5), properties.lightIntensity5);
    light5.position.set(-1000, -10000, 0);
    this.scene.add(light5);
    const light6 = new THREE.PointLight(new THREE.Color(properties.lightColor6), properties.lightIntensity6);
    light6.position.set(1000, 0, -10000);
    this.scene.add(light6);
    const light7 = new THREE.PointLight(new THREE.Color(properties.lightColor7), properties.lightIntensity7);
    light7.position.set(-1000, 0, 10000);
    this.scene.add(light7);

    // init postprocessing
    this.composer = new PostProcessing.EffectComposer(renderer);
    const renderPass = new PostProcessing.RenderPass(this.scene, this.camera);
    // renderPass.renderToScreen = true;
    this.composer.addPass(renderPass);
    this.shaderPass = new PostProcessing.ShaderPass(ContrastShader);
    this.shaderPass.renderToScreen = true;
    this.composer.addPass(this.shaderPass);

    // const gui = new dat.GUI();
    // const f1 = gui.addFolder('cam pos')
    // f1.add(this.camera.position, 'x').min(-10000).max(10000);
    // f1.add(this.camera.position, 'y').min(-10000).max(10000);
    // f1.add(this.camera.position, 'z').min(-10000).max(10000);
    //
    // const f2 = gui.addFolder('cam look');
    // f1.add(this.lookAt, 'x').min(-10000).max(10000).onChange((value) => this.camera.lookAt(this.lookAt));
    // f1.add(this.lookAt, 'y').min(-10000).max(10000).onChange((value) => this.camera.lookAt(this.lookAt));
    // f1.add(this.lookAt, 'z').min(-10000).max(10000).onChange((value) => this.camera.lookAt(this.lookAt));
    //
    // f1.open();
  }

  updateShader(row, audioBuffer) {
    const audioAverage = audioBuffer.reduce((previous, current) => current += previous) / audioBuffer.length;
    const audioMin = audioBuffer.reduce((previous, current) => Math.min(current, previous));
    const audioMax = audioBuffer.reduce((previous, current) => Math.min(current, previous));
    const contrast = (audioMax / 128) * 0.8;
    const brightness = ((audioMin / 128) * 0.2) - 0.07;
    let multiplier = 1;

    // fade out after 208
    const fadeInLength = 16;
    const fadeoutLength = 32;
    if (row < fadeInLength) {
      if (!this.fadeinStart) {
        this.fadeinStart = performance.now();
      }
      this.fadeinTime = performance.now() - this.fadeinStart;
      multiplier = this.interpolate(4, 1, this.fadeinTime, ROW_LENGTH * fadeInLength);
    } else if (row >= DEMO_LENGTH - fadeoutLength) {
      if (!this.fadeoutStart) {
        this.fadeoutStart = performance.now();
      }
      this.fadeoutTime = performance.now() - this.fadeoutStart;
      multiplier = this.interpolate(1, 12, this.fadeoutTime, ROW_LENGTH * fadeoutLength);
    }

    this.shaderPass.uniforms.contrast.value = contrast;
    this.shaderPass.uniforms.brightness.value = Math.max(-1, -brightness * multiplier);
  }

  /**
   * @param time float
   * @param row int
   * @param audioBuffer Uint8Array
   */
  render(row, audioBuffer) {
    this.updateShader(row, audioBuffer);

    // Log values when using dat gui for prototyping
    // console.log(JSON.stringify({
    //   position: this.camera.position,
    //   look: this.lookAt
    // }));

    // don't interpolate on bar changes
    if (row % (ROWS_PER_BEAT * BEATS_PER_BAR) === 0) {
      this.targetCameraX = this.sourceCameraX = this.camera.position.x =
        this.syncDevice.getTrack('cameraX').getValue(row);
      this.targetCameraY = this.sourceCameraY = this.camera.position.y =
        this.syncDevice.getTrack('cameraY').getValue(row);
      this.targetCameraZ = this.sourceCameraZ = this.camera.position.z =
        this.syncDevice.getTrack('cameraZ').getValue(row);
      this.targetCameraLookX = this.sourceCameraLookX = this.lookAt.x =
        this.syncDevice.getTrack('cameraLookX').getValue(row);
      this.targetCameraLookY = this.sourceCameraLookY = this.lookAt.y =
        this.syncDevice.getTrack('cameraLookY').getValue(row);
      this.targetCameraLookZ = this.sourceCameraLookZ = this.lookAt.z =
        this.syncDevice.getTrack('cameraLookZ').getValue(row);
    } else {
      if (this.currentRow !== row) {
        this.rowTime = performance.now();

        this.sourceCameraX = this.camera.position.x;
        this.sourceCameraY = this.camera.position.y;
        this.sourceCameraZ = this.camera.position.z;
        this.sourceCameraLookX = this.lookAt.x;
        this.sourceCameraLookY = this.lookAt.y;
        this.sourceCameraLookZ = this.lookAt.z;
        this.targetCameraX = this.syncDevice.getTrack('cameraX').getValue(row);
        this.targetCameraY = this.syncDevice.getTrack('cameraY').getValue(row);
        this.targetCameraZ = this.syncDevice.getTrack('cameraZ').getValue(row);
        this.targetCameraLookX = this.syncDevice.getTrack('cameraLookX').getValue(row);
        this.targetCameraLookY = this.syncDevice.getTrack('cameraLookY').getValue(row);
        this.targetCameraLookZ = this.syncDevice.getTrack('cameraLookZ').getValue(row);
      }
      this.timeSinceRow = performance.now() - this.rowTime;
      this.camera.position.x = this.interpolate(this.sourceCameraX, this.targetCameraX, this.timeSinceRow, ROW_LENGTH);
      this.camera.position.y = this.interpolate(this.sourceCameraY, this.targetCameraY, this.timeSinceRow, ROW_LENGTH);
      this.camera.position.z = this.interpolate(this.sourceCameraZ, this.targetCameraZ, this.timeSinceRow, ROW_LENGTH);
      this.lookAt.x = this.interpolate(this.sourceCameraLookX, this.targetCameraLookX, this.timeSinceRow, ROW_LENGTH);
      this.lookAt.y = this.interpolate(this.sourceCameraLookY, this.targetCameraLookY, this.timeSinceRow, ROW_LENGTH);
      this.lookAt.z = this.interpolate(this.sourceCameraLookZ, this.targetCameraLookZ, this.timeSinceRow, ROW_LENGTH);
    }

    this.camera.lookAt(this.lookAt);
    this.composer.render(this.scene, this.camera);
    this.currentRow = row;
  }
}
